import { HavaDeligiConfig, HavaDeligiTipi } from '../FikoSPHParametreler';
import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';
import { Vector3 } from 'three';

/**
 * FikoHavaDeligi - Air vent element for SPH fluid simulation
 * GPU struct size: 64 bytes
 *
 * Layout:
 * - pozX, pozY, pozZ, cap (16 bytes)
 * - yonX, yonY, yonZ, derinlik (16 bytes)
 * - geriBasinc, akisDirenci, gecirgenlik, aktif as u32 (16 bytes)
 * - tipi as u32, acilmaZamani, kapanmaZamani, pad (16 bytes)
 */
export class FikoHavaDeligi implements ISPHEleman {
  readonly id: string;
  readonly tip: ElemanTipi = ElemanTipi.HAVA_DELIGI;

  pozisyon: Vector3;
  yon: Vector3;
  cap: number;
  derinlik: number;
  geriBasinc: number;
  akisDirenci: number;
  gecirgenlik: number;
  tipi: HavaDeligiTipi;
  aktif: boolean;
  acilmaZamani: number;
  kapanmaZamani: number;
  ad: string;
  renk?: string;
  gorunur?: boolean;

  constructor(config: Partial<HavaDeligiConfig> = {}) {
    this.id = config.id || olusturId();

    this.pozisyon = config.pozisyon ? new Vector3(config.pozisyon.x, config.pozisyon.y, config.pozisyon.z) : new Vector3(0, 0, 0);
    this.yon = config.yon ? new Vector3(config.yon.x, config.yon.y, config.yon.z).normalize() : new Vector3(0, 1, 0);
    this.cap = config.cap ?? 0.01;
    this.derinlik = config.derinlik ?? 0.05;
    this.geriBasinc = config.geriBasinc ?? 101325; // 1 atm
    this.akisDirenci = config.akisDirenci ?? 0.1;
    this.gecirgenlik = config.gecirgenlik ?? 0;
    this.tipi = config.tipi ?? HavaDeligiTipi.ACIK;
    this.aktif = config.aktif ?? true;
    this.acilmaZamani = config.acilmaZamani ?? 0;
    this.kapanmaZamani = config.kapanmaZamani ?? Infinity;
    this.ad = config.ad || 'Hava Deliği';
    this.renk = '#00BFFF';
    this.gorunur = config.gorunur ?? true;
  }

  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);
    let offset = 0;

    // Chunk 1: pozX, pozY, pozZ, cap (16 bytes)
    view.setFloat32(offset, this.pozisyon.x, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.y, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.z, true); offset += 4;
    view.setFloat32(offset, this.cap, true); offset += 4;

    // Chunk 2: yonX, yonY, yonZ, derinlik (16 bytes)
    view.setFloat32(offset, this.yon.x, true); offset += 4;
    view.setFloat32(offset, this.yon.y, true); offset += 4;
    view.setFloat32(offset, this.yon.z, true); offset += 4;
    view.setFloat32(offset, this.derinlik, true); offset += 4;

    // Chunk 3: geriBasinc, akisDirenci, gecirgenlik, aktif as u32 (16 bytes)
    view.setFloat32(offset, this.geriBasinc, true); offset += 4;
    view.setFloat32(offset, this.akisDirenci, true); offset += 4;
    view.setFloat32(offset, this.gecirgenlik, true); offset += 4;
    view.setUint32(offset, this.aktif ? 1 : 0, true); offset += 4;

    // Chunk 4: tipi as u32, acilmaZamani, kapanmaZamani, pad (16 bytes)
    // Map string enum to numeric for GPU
    const tipiNumeric = this.tipi === HavaDeligiTipi.ACIK ? 0 : this.tipi === HavaDeligiTipi.FILTRELI ? 1 : 2;
    view.setUint32(offset, tipiNumeric, true); offset += 4;
    view.setFloat32(offset, this.acilmaZamani, true); offset += 4;
    view.setFloat32(offset, this.kapanmaZamani, true); offset += 4;
    view.setUint32(offset, 0, true); // pad

    return buffer;
  }

  gpuStructBoyut(): number {
    return 64;
  }

  toJSON(): any {
    return {
      id: this.id,
      tip: this.tip,
      pozisyon: { x: this.pozisyon.x, y: this.pozisyon.y, z: this.pozisyon.z },
      yon: { x: this.yon.x, y: this.yon.y, z: this.yon.z },
      cap: this.cap,
      derinlik: this.derinlik,
      geriBasinc: this.geriBasinc,
      akisDirenci: this.akisDirenci,
      gecirgenlik: this.gecirgenlik,
      tipi: this.tipi,
      aktif: this.aktif,
      acilmaZamani: this.acilmaZamani,
      kapanmaZamani: this.kapanmaZamani,
      ad: this.ad,
      gorunur: this.gorunur
    };
  }

  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.cap <= 0) {
      hatalar.push('Çap pozitif olmalıdır');
    }

    if (this.derinlik <= 0) {
      hatalar.push('Derinlik pozitif olmalıdır');
    }

    if (this.geriBasinc < 0) {
      hatalar.push('Geri basınç negatif olamaz');
    }

    if (this.akisDirenci < 0) {
      hatalar.push('Akış direnci negatif olamaz');
    }

    if (this.gecirgenlik < 0 || this.gecirgenlik > 1) {
      hatalar.push('Geçirgenlik 0-1 arasında olmalıdır');
    }

    if (this.yon.lengthSq() < 0.99 || this.yon.lengthSq() > 1.01) {
      hatalar.push('Yön vektörü normalize edilmeli');
    }

    if (this.acilmaZamani < 0) {
      hatalar.push('Açılma zamanı negatif olamaz');
    }

    if (this.kapanmaZamani < this.acilmaZamani) {
      hatalar.push('Kapanma zamanı açılma zamanından küçük olamaz');
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  klonla(): FikoHavaDeligi {
    return new FikoHavaDeligi({
      pozisyon: this.pozisyon.clone(),
      yon: this.yon.clone(),
      cap: this.cap,
      derinlik: this.derinlik,
      geriBasinc: this.geriBasinc,
      akisDirenci: this.akisDirenci,
      gecirgenlik: this.gecirgenlik,
      tipi: this.tipi,
      aktif: this.aktif,
      acilmaZamani: this.acilmaZamani,
      kapanmaZamani: this.kapanmaZamani,
      ad: this.ad + ' (Kopya)',
      gorunur: this.gorunur
    });
  }

  parametreListesi(): string[] {
    return [
      'pozisyon', 'yon', 'cap', 'derinlik', 'geriBasinc',
      'akisDirenci', 'gecirgenlik', 'tipi', 'aktif',
      'acilmaZamani', 'kapanmaZamani', 'ad', 'renk', 'gorunur'
    ];
  }

  parametreAyarla(anahtar: string, deger: any): boolean {
    switch (anahtar) {
      case 'pozisyon':
        if (deger instanceof Vector3) {
          this.pozisyon.copy(deger);
          return true;
        }
        break;
      case 'yon':
        if (deger instanceof Vector3) {
          this.yon.copy(deger).normalize();
          return true;
        }
        break;
      case 'cap':
      case 'derinlik':
      case 'geriBasinc':
      case 'akisDirenci':
      case 'gecirgenlik':
      case 'acilmaZamani':
      case 'kapanmaZamani':
        if (typeof deger === 'number') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'tipi':
        // Accept both string and number for enum assignment
        (this as any).tipi = deger;
        return true;
      case 'aktif':
      case 'gorunur':
        if (typeof deger === 'boolean') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'ad':
      case 'renk':
        if (typeof deger === 'string') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
    }
    return false;
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(anahtar: string): any {
    return (this as any)[anahtar];
  }
}

/**
 * Factory function to create default air vent element
 */
export function varsayilanHavaDeligi(config?: Partial<HavaDeligiConfig>): FikoHavaDeligi {
  return new FikoHavaDeligi(config);
}
